import { useMemo } from "react";
import { Task } from "@common/types/task";
import { FinancialAccount } from "@common/types/financialAccount";
import { normalizeAccountNumber } from "@common/helpers/accountNumberMatch";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";

interface ExpectedAccountNumberInfo {
    // Distinct candidate numbers to match against (account number mask).
    // Empty when there is no constraint.
    candidates: readonly string[];
    // The resolved financial account for this task, if any.
    account: FinancialAccount | undefined;
    // True while the entities query is still resolving. See
    // `getDocVerificationStatus` for why callers must defer verified-placement
    // during this window.
    isLoading: boolean;
}

export function useExpectedAccountNumberCandidates(
    task: Task,
): ExpectedAccountNumberInfo {
    const expectedAccountId = task.view?.filters?.accountIds?.[0];
    const entitiesContexts = useEntitiesContexts();
    const taskEntityIds = task.entityIds;

    return useMemo(() => {
        if (expectedAccountId === undefined) {
            return { candidates: [], account: undefined, isLoading: false };
        }
        if (entitiesContexts === undefined) {
            return { candidates: [], account: undefined, isLoading: true };
        }
        const account = entitiesContexts
            .filter(
                (ec) => !taskEntityIds || taskEntityIds.includes(ec.entity.id),
            )
            .flatMap((ec) => ec.financialAccounts)
            .find((a) => a.id === expectedAccountId);

        if (!account) {
            return { candidates: [], account: undefined, isLoading: false };
        }

        const candidates: string[] = [];
        if (account.accountNumberMask) {
            const normalized = normalizeAccountNumber(
                account.accountNumberMask,
            );
            if (normalized) {
                candidates.push(account.accountNumberMask);
            }
        }

        return { candidates, account, isLoading: false };
    }, [entitiesContexts, expectedAccountId, taskEntityIds]);
}
